import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { IAdditionalSetting } from '@app/Models/IAdditionalSetting';
import { DataImportMapping } from '@app/Tenant/Applications/Airsweb/Models/Imports/data-import-mapping';

export class StateItem {
    Id: number;
    Name: string;
}

export class FormatViewModel {
    Id: number;
    Name: string;
    Extensions: string;
}
export class NewImportViewModel {
    ImportType: any;
    ImportMapping: any;
    Id: number;
    Formats: FormatViewModel[];
    ErrorRows: RawRow[];
    WarningRows: RawRow[];
    NodeErrors: NodeError[];
    ValidationComplete: boolean;
    FormatError: string;
    FormatErrors: string[];
    SelectedModuleId: string;
    SelectedStateId: string;
    AvailableStates: StateItem[];
    Title: string;
    Top5Rows: string[];
    ValidationError: string;
    Failed: boolean;
    ImportLocation: any;
    FileImportCount: number;
    FileImportMessage: string;
    FormFlowFields: any[] = [];
    CustomFieldMappings: any[] = [];
    HeaderRows: any;
    LevelCounts: any[];
    ValidationErrorDetail: string;
    AdditionalSettings: IAdditionalSetting[];
    Modules: any[];
}

export class RowError {
    ColumnIndex: number;
    Message: string;
    ToString(): string {
        return this.Message;
    }
}
export class RawRow {
    Fields: string[];
    Errors: RowError[];
    Warnings: RowError[];
    RowNumber: number;
}

export class NodeError {
    NodeDescription: string;
    Error: string;
}

export class ValidationResult {
    Errors: string[];
    SuccessMessages: string[];
    Success: boolean;
    Model: any;
}

export class ModuleDto {
    ModuleId: string;
    ModuleName: string;
}

export interface ColumnMapping {
    InputColumnsIndex: number;
    InputColumnName: string;
    PropertyName: string;
    IsCustomField: boolean;
    MultiFieldName: string;
    TypeName: string;
}

export interface CustomMapping {
    Id: number;
    Name: string;
    ColumnMappings: ColumnMapping[];
    ModuleId: string;
    TenantId: string;
}


@Injectable({
    providedIn: 'root',
})
export class ImportService {
    constructor(private http: HttpClient) {

    }

    private apiUrl = '/api/import/';
    private apiFeatureUrl = '/api/feature/';

    private handleError(error: any): Promise<any> {
        return Promise.reject(error.message || error);
    }

    getFormat(id: number){
        return this.http.get<DataImportMapping>(`${this.apiUrl}format?id=${id}`)
    }

    uploadFile(file: File, importType: string, format: FormatViewModel, id: number, selectedModuleId: string, fileName: string, customFieldMappingId: number = 0) {
        const formData = new FormData();
        formData.append('uploadFile', file, file.name);

        const headers = new HttpHeaders();
        headers.append('Accept', 'application/json');
        const options = { headers };
        let params: any = {
            formatId: format.Id,
            importId: id,
            fileName: fileName,
        };

        if (selectedModuleId) {
            params.moduleId = selectedModuleId;
        }
        if (customFieldMappingId !== undefined && customFieldMappingId !== null) {
            params.customFieldMappingId = customFieldMappingId;
        }
        const queryString = new URLSearchParams(params).toString();
        return this.http.post(`${this.apiUrl}upload/?${queryString}`, formData, options);
    }

    uploadFileFromLocation(folderLocation: string, pattern: string, importType: string, format: FormatViewModel, id: number, selectedModuleId: string, customFieldMappingId: number = 0) {
        const formData = new FormData();

        const headers = new HttpHeaders();
        headers.append('Accept', 'application/json');
        const options = { headers };
        const moduleIdParam = selectedModuleId ? `&moduleId=${selectedModuleId}` : '';
        return this.http.post(`${this.apiUrl}uploadFromLocation/?formatId=${format.Id}&importId=${id}${moduleIdParam}&customFieldMappingId=${customFieldMappingId}&folderLocation=${folderLocation}&pattern=${pattern}`, formData, options);
    }

    getNewViewModel(importType: string, tenantId: string) {
        return this
            .http
            .get<NewImportViewModel>(`${this.apiUrl}new/${importType}/?tenantId=${tenantId}`);
    }

    getFormats(importType: string, tenantId: string, moduleId: string) {
        const moduleIdParam = moduleId ? `&moduleId=${moduleId}` : '';
        const url = `${this.apiUrl}formats/?type=${importType}&tenantId=${tenantId}${moduleIdParam}`;
        return this.http.get<FormatViewModel[]>(url);
    }

    getImportStates(tenantId: string, moduleId: string) {
        const moduleIdParam = moduleId ? `&moduleId=${moduleId}` : '';
        const url = `${this.apiUrl}importstates/?tenantId=${tenantId}${moduleIdParam}`;
        return this.http.get<StateItem[]>(url);
    }

    validate(id: number, moduleId: string, importStateId: string) {
        let url = `${this.apiUrl}validate/${id}`;

        if (moduleId) {
            url += `/${moduleId}`;
        }

        if (moduleId && importStateId) {
            url += `/${importStateId}`;
        }

        return this.http.get<NewImportViewModel>(url);
    }

    validateHistoricalData(id: number, moduleId: string, importStateId: string, customFieldMappingId: number, includeAllErrors: boolean = false, includeValidRowsInReport: boolean = false) {
        let url = `${this.apiUrl}validate/${id}`;

        if (moduleId) {
            url += `/${moduleId}`;
        }

        if (moduleId && importStateId) {
            url += `/${importStateId}`;
        }

        if (customFieldMappingId) {
            url += `/${customFieldMappingId}`;
        }

        url += `/?limitErrors=${!includeAllErrors}&includeGoodRowsInReport=${includeValidRowsInReport}`;
        return this.http.get<NewImportViewModel>(url);
    }

    beginImport(id: number, filename: string, additionals: any) {
        return this
            .http
            .post<ValidationResult>(`${this.apiUrl}import/${id}?filename=${filename}`, additionals);
    }

    getLatestFileContentsFromFolder(tenantName: string, folderLocation: string, filePattern: string) {
        const url = `${this.apiUrl}getlatestfilecontentsfromfolder?tenantName=${encodeURIComponent(tenantName)}&folderLocation=${encodeURIComponent(folderLocation)}&filePattern=${encodeURIComponent(filePattern)}`;
        return this.http.get<any>(url);
    }

    getErrorReportFile(viewModelId: number) {
        return this.http.get(`${this.apiUrl}errorFile/${viewModelId}`, {
            responseType: "arraybuffer"
        })
    }
}
